import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useFormApi } from '@lamesarv-sdk/hooks';
import { useDevice } from '@/hooks/useDevice';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { navigate, states } from '@lamesarv-sdk/tools';
import { isFormDynamicsLocationEnabled } from '@/tools/forms';
import { parsePhone } from '@/tools/dynamics';
import { withSuspense } from '@/tools/withSuspense';

import { RichText } from '@lamesarv-sdk/components';
import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';
import { CommonSelect } from '@/components/CommonForm/CommonSelect';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';

import { BaseOptInCheckbox, BaseSubmit } from '../base';
import { IFormSchema, validationSchema } from './schema';

import { IFormInput } from '@lamesarv-sdk/types';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFormMapping } from '@/types/dynamicsForms';
import { FormListTag, FormTag } from '@/types/forms';
import { IFormProps, IFormResetterProps } from '../types';

import * as FormClasses from '../classes';

const formStyles = {
  container: 'leading-none grow relative',
  label:
    'absolute left-3 top-3 pointer-events-none leading-none text-black-400 text-sm md:text-md text-left [&>span]:text-md [&>span]:leading-none transition-all group-focus-within:left-1 group-focus-within:top-1 group-focus-within:-translate-y-0 group-focus-within:text-xs transition-all',
  labelWithValue: 'left-1 top-1 -translate-y-0 text-xs',
  input:
    'leading-none px-3 py-3 rounded-sm focus:outline-none focus:ring-0 focus:border-black-500 focus:px-3 focus:pt-5 focus:pb-1',
  inputWithValue: 'px-3 pt-5 pb-1',
  button:
    'capitalize rounded-md bg-blue-500 text-white border-none hover:bg-blue-400 hover:text-white hover:border-none',
};

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export const FormContactSell = withSuspense((props: IFormProps) => {
  const { context: layoutContext } = useContext(LayoutContext);
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const { env } = useEnv();

  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();

  const getLocationDetails = (location: string) => {
    const [city, stateCode] = location.split(',').map((item) => item.trim());
    const state = states.find((state) => state.postal === stateCode.toLowerCase())?.name || stateCode;

    return { city, state };
  };

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const { city, state } = getLocationDetails(formInput.location);

    const formMappings: IDynamicsFormMapping[] = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: String(DynamicsLeadType.buyer),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      { FormFieldName: 'make', DataverseFieldName: 'lmrv_make' },
      { FormFieldName: 'year', DataverseFieldName: 'lmrv_vehicleyear' },
      { FormFieldName: 'model', DataverseFieldName: 'lmrv_model' },
      { FormFieldName: 'miles', DataverseFieldName: 'lmrv_miles' },
      { FormFieldName: 'firstName', DataverseFieldName: 'firstname' },
      { FormFieldName: 'lastName', DataverseFieldName: 'lastname' },
      { FormFieldName: 'email', DataverseFieldName: 'emailaddress1' },
      { FormFieldName: 'zipCode', DataverseFieldName: 'address1_postalcode' },
      { DataverseFieldName: 'address1_city', DataverseFieldValue: city },
      { DataverseFieldName: 'address1_stateorprovince', DataverseFieldValue: state },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.phone) },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue:
          String(props.locationsOptionSet.find((item) => item.Label === formInput.location)?.DataverseValue) || '',
      },
      {
        DataverseFieldName: 'lmrv_url',
        DataverseFieldValue: typeof location !== 'undefined' ? location?.href : '',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      formInput.ip = device.ip;
      formInput.user_agent = device.userAgent;
      formInput.platform = device.platform;
      formMappings.push(
        { DataverseFieldName: 'lmrv_ip', DataverseFieldValue: device.ip },
        { DataverseFieldName: 'lmrv_useragent', DataverseFieldValue: device.userAgent },
        { DataverseFieldName: 'lmrv_platform', DataverseFieldValue: device.platform },
      );
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!layoutContext.geoLocation) {
      formInput.geo_coordinates = layoutContext.geoLocation.join(',');
      formMappings.push({
        DataverseFieldName: 'lmrv_geocoordinates',
        DataverseFieldValue: layoutContext.geoLocation.join(','),
      });
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      formInput.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      formInput.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
      formMappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: formInput.captcha_score,
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: formInput.captcha_threshold,
        },
      );
    }

    if (env.ENABLE_FORMS_LEGACY) {
      sendFormLegacy({
        ...formInput,
        formType: 'custom',
        leadType: formInput.contactType === 'trade-in' ? 'seller' : 'buyer',
        leadSource: 'lamesarv.com',
        city,
        state,
        comments: '',
        extra_list_ids: [FormListTag.MASTER],
        extra_tag_ids: [
          formInput.contactType === 'trade-in' ? FormTag.CUSTOM_BUCKET_INTERNET : FormTag.CUSTOM_BUCKET_BUYER,
          FormTag.SELL_TRADE_MY_RV,
        ],
      } as IFormInput);
    }

    if (env.ENABLE_FORMS_DYNAMICS && isFormDynamicsLocationEnabled(env, formInput.location)) {
      await sendForm({
        formId: `#${props.custom_id || 'formContactSell'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_CONTACT_SELL,
      });
    }
  };

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;

  // -- Prevent rendering if neither form system is enabled
  if (!env.ENABLE_FORMS_DYNAMICS && !env.ENABLE_FORMS_LEGACY) {
    return null;
  }

  return (
    <CommonForm
      id={props.custom_id || 'formContactSell'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-contact-sell` : 'form-contact-sell'}
    >
      <CommonHidden name="formId" value="25" />
      <CommonHidden name="formType" value="seller" />
      <CommonHidden name="leadType" value="buyer" />
      <CommonHidden name="leadSource" value="lamesarv.com" />
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge('w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-2', props.classes_header)}
        >
          {props.title && <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>}
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-2 bg-white', props.classes_container)}>
        {!props.extract_header && (
          <div className={twMerge('flex flex-col gap-4', props.classes_header)}>
            {props.title && (
              <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>
            )}
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </div>
        )}
        {_loading && (
          <div
            className={
              'flex items-center w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faClock} className="w-8 mr-2" />
            <span>Sending...</span>
          </div>
        )}
        {_submitted && !_error && (
          <div
            className={
              'flex items-center w-full bg-green-100 border border-green-500 text-green-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="w-8 mr-2" />
            <span
              data-tag-group="form-success-label"
              data-tag={
                props.data_tag
                  ? `${props.data_tag}--form-contact-sell-success-label`
                  : 'form-contact-sell-success-label'
              }
            >
              Thank you for requesting more information, a member of our Buying Team will reach out to you shortly.
            </span>
          </div>
        )}
        {_submitted && _error && (
          <div
            className={
              'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
            <span>An error has occurred, please try again later or contact us using alternative methods.</span>
          </div>
        )}
        <div className={twMerge('flex flex-col', props.classes_content)}>
          <div className="flex flex-row flex-wrap -mx-2">
            <h3 className="mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Tell Us About Your RV</h3>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput type="text" label="Make" placeholder="Make*" name="make" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput type="text" label="Model" placeholder="Model*" name="model" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput
                type="number"
                label="Year*"
                placeholder="Year"
                name="year"
                customOptions={{ min: 0, step: 1 }}
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full md:w-2/4">
              <CommonInput
                type="number"
                label="Miles"
                placeholder="Miles*"
                name="miles"
                customOptions={{ min: 0, step: 1 }}
                isRequired
                hideLabel
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap -mx-2">
            <h3 className="my-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Tell Us How We Can Reach You</h3>
            <div className="my-2 px-2 w-full">
              <CommonSelect
                name="contactType"
                label="Select Sell or Trade-In"
                options={[
                  { title: 'Sell', value: 'sell' },
                  { title: 'Trade-In', value: 'trade-in' },
                ]}
                isRequired
                classContainer={formStyles.container}
                classLabel={formStyles.label}
                classLabelWithValue={formStyles.labelWithValue}
                classInput={twMerge(formStyles.input, props.classes_input)}
                classInputWithValue={formStyles.inputWithValue}
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                type="text"
                label="First Name"
                placeholder="First Name*"
                name="firstName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                type="text"
                label="Last Name"
                placeholder="Last Name*"
                name="lastName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput type="email" label="Email" placeholder="Email" name="email" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonMaskInput
                type="tel"
                mask="(999) 999-9999"
                label="Phone"
                placeholder="Phone*"
                name="phone"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonLocationSelect
                label="Preferred Store"
                name="location"
                locationsOptionSet={props.locationsOptionSet}
                isRequired
                classContainer={formStyles.container}
                classLabel={formStyles.label}
                classLabelWithValue={formStyles.labelWithValue}
                classInput={twMerge(formStyles.input, props.classes_input)}
                classInputWithValue={formStyles.inputWithValue}
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput type="text" label="Zip Code" placeholder="Zip Code*" name="zipCode" isRequired hideLabel />
            </div>
          </div>
          {props.display_sms_checkbox && (
            <div className="flex flex-row py-2">
              <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
            </div>
          )}
          <div className="flex flex-row">
            <div className="mt-2">
              <BaseSubmit {...props} formNameSlug="form-contact-sell" styleClasses={FormClasses.buttonNormalClasses} />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
